<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import BaseBubble from './Base.vue';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import GroupParticipantName from '../GroupParticipantName.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import { ATTACHMENT_TYPES } from '../constants';
import { downloadFile } from '@chatwoot/utils';

const emit = defineEmits(['error']);
const { t } = useI18n();
const hasError = ref(false);
const showGallery = ref(false);
const isDownloading = ref(false);
const { filteredCurrentChatAttachments, attachments } = useMessageContext();

const handleError = () => {
  hasError.value = true;
  emit('error');
};

const attachment = computed(() => {
  return attachments.value[0];
});

const isReel = computed(() => {
  return attachment.value.fileType === ATTACHMENT_TYPES.IG_REEL;
});

const downloadAttachment = async () => {
  const { fileType, dataUrl, extension } = attachment.value;
  try {
    isDownloading.value = true;
    await downloadFile({
      url: dataUrl,
      type: fileType,
      extension: extension || 'mp4',
    });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-3"
    data-bubble-name="video"
    @click="!hasError && (showGallery = true)"
  >
    <GroupParticipantName />
    <div
      v-if="hasError"
      class="flex flex-col items-center gap-2 p-4 text-center rounded-lg bg-n-slate-3"
    >
      <Icon icon="i-lucide-video-off" class="size-8 text-n-slate-11" />
      <p class="mb-0 text-n-slate-11 text-sm">
        {{ $t('COMPONENTS.MEDIA.VIDEO_UNAVAILABLE') }}
      </p>
      <Button
        xs
        faded
        slate
        icon="i-lucide-download"
        :is-loading="isDownloading"
        :disabled="isDownloading"
        @click.stop="downloadAttachment"
      >
        {{ $t('COMPONENTS.MEDIA.DOWNLOAD_ANYWAY') }}
      </Button>
    </div>
    <div v-else class="relative group rounded-lg overflow-hidden">
      <div
        v-if="isReel"
        class="absolute p-2 flex items-start justify-end right-0 pointer-events-none"
      >
        <Icon icon="i-lucide-instagram" class="text-white shadow-lg" />
      </div>
      <video
        controls
        class="rounded-lg skip-context-menu"
        :src="attachment.dataUrl"
        :class="{
          'max-w-48': isReel,
          'max-w-full': !isReel,
        }"
        @error="handleError"
      />
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="onError"
    @close="() => (showGallery = false)"
  />
</template>
